<template>
  <div class="maxcontainer container">
    <PageLoader v-if="loading"/>
      <SmartFeedConsolidatedData
        v-if="userLevel !== 'employee' && showConsolidatedChart && batch"
        :class="{ 'is-refreshing': isReloading, 'just-refreshed': showRefreshAnimation }"
        :workingSensors="workingSensors"
        :startDate="start_date"
        :endDate="end_date"
        :seekNewRange="seekNewRange"
        :allFoods="allFoods"
      />
      <div >
      <div class="container-dates" v-if="showProfileVisualization">
        <div class="container-inputsDate">
          <span>De:</span>
          <input class="input-date" v-model="start_date"  type="datetime-local" :max="end_date" >
        </div>
        <div class="container-inputsDate">
          <span>Até:</span>
          <input class="input-date" v-model="end_date" type="datetime-local" :min="start_date">
        </div>
        <div class="container-buton">
          <button class="buttomDate" :disabled="isReloading" @click="newRangeData">
            {{ isReloading ? 'Atualizando...' : 'Atualizar periodo' }}
          </button>
        </div>
        <div class="container-racao" v-if="!showConsolidatedChart">
          <button 
            class="button-food" 
            data-toggle="modal"
            data-target="#foodModal">
              Materia Prima
          </button>
        </div>
        <div class="container-export">
          <button 
            class="export-btn" 
            @click="openExportModal" 
            :disabled="loadingExport"
            title="Exportar histórico de perfis"
          >
            <i :class="loadingExport ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
            <span>{{ loadingExport ? 'Gerando...' : 'Exportar' }}</span>
          </button>
        </div>
        <ExportProfileModal
          :is-open="showExportModal"
          :loading="loadingExport"
          @close="closeExportModal"
          @confirm="onExportConfirm"
        />
      </div>

      <!-- Banner de atualização de período -->
      <transition name="reload-banner">
        <div v-if="isReloading" class="reload-banner" role="status" aria-live="polite">
          <span class="reload-spinner" aria-hidden="true"></span>
          <span class="reload-text">Carregando novos dados...</span>
        </div>
      </transition>

      <div
        class="smartfeed-data-container"
        :class="{ 'is-refreshing': isReloading, 'just-refreshed': showRefreshAnimation }"
      >
        <div
          class="smartfeed-card-wrapper"
          :class="{ 'smartfeed-card-wrapper--legacy': useLegacySiloProfileVisualization }"
          v-for="sensor in sensorsToRender"
          :key="getSensorId(sensor)"
          :data-silo="sensor.silo"
          :data-sensor="getSensorId(sensor)"
        >
          <SmartFeed
            :seekNewRange="seekNewRange"
            :startDate="start_date"
            :foodByCompany="allFoods"
            :endDate="end_date"
            :sensor="sensor"
            :useInformedWeight="useInformedWeight"
            :sacaWeight="sacaWeight"
            :showProfileVisualization="showProfileVisualization"
            :useLegacySiloProfileVisualization="useLegacySiloProfileVisualization"
            :barnProp="barn"
            :farmPk="farmPk"
            :AIcombinedSensors="AIcombinedSensors"
          />
          <OpenBalenaCard
            v-for="siloSensor in balenaSensorsForSilo(sensor)"
            :key="`ob-${getSensorId(siloSensor)}`"
            :sensor="siloSensor"
            :label="balenaSensorsForSilo(sensor).length > 1 ? (siloSensor.name || `Sensor ${getSensorId(siloSensor)}`) : null"
          />
        </div>
      </div>
    </div>
    <ModalFood :selectedFeed="selectedFeed" :allFood="allFoods"/>
  </div>
</template>

<script>
import SmartFeed from "./SmartFeedV3.vue";
import SmartFeedConsolidatedData from "./SmartFeedConsolidatedData.vue";
import PageLoader from "../components/Loader.vue"
import ModalFood from "../components/ModalFood.vue"
import ExportProfileModal from "./ExportProfileModal.vue"
import OpenBalenaCard from "./OpenBalenaCard.vue"
import { format, subDays} from 'date-fns';
import { sensorScreenBlocked } from '../utils/feedInventoryAccess';
import { generateMultipleSilosExcel } from '../excel/excelSiloData';
import { generateMultipleSilosPDF } from '../pdf/pdfSiloData';
import {
  filterProfilesByMinLevelVariation,
  getMinSiloLevelVariationPercent,
} from '../utils/siloLevelVariationFilter';

export default {
  name: "Navbar",
  components: {
    SmartFeed,
    SmartFeedConsolidatedData,
    PageLoader,
    ModalFood,
    ExportProfileModal,
    OpenBalenaCard
  },
  data(){
    // Quando a navegação vem da Home com um silo/sensor específico, já entramos
    // em "modo prioridade" — assim só esse card é montado de cara, e os demais
    // só aparecem depois que o card prioritário teve tempo de buscar seus dados.
    const queryHasPriorityHint =
      (this.$route?.query?.silo != null && this.$route?.query?.silo !== '') ||
      (this.$route?.query?.sensor != null && this.$route?.query?.sensor !== '');
    return{
      loading: true,
      start_date: '',
      end_date: '',
      seekNewRange: false,
      showExportModal: false,
      loadingExport: false,
      shouldRenderRemainingSensors: !queryHasPriorityHint,
      priorityRenderTimeout: null,
      // Estado da UI ao clicar em "Atualizar periodo":
      // isReloading mostra o banner "Carregando novos dados..." e esmaece os cards.
      // showRefreshAnimation aplica um pulse/fade-in nos cards quando os dados voltam.
      isReloading: false,
      showRefreshAnimation: false,
      reloadBannerTimeout: null,
      refreshAnimationTimeout: null,
    }
  },
  methods: {
    getDefaultDateRange() {
      const today = new Date()
      const isBatchClosed = this.batch?.date_departure != null
      const referenceDate = isBatchClosed
        ? new Date(this.batch.date_departure)
        : today

      let startDate = subDays(referenceDate, 3)

      const accommodationDate = this.batch?.date_accomodation
        ? new Date(this.batch.date_accomodation)
        : null

      if (accommodationDate && startDate < accommodationDate) {
        startDate = accommodationDate
      }

      const dateTimeFormat = "yyyy-MM-dd'T'HH:mm:ss"
      return {
        start: format(startDate, dateTimeFormat),
        end: format(referenceDate, dateTimeFormat),
      }
    },
    getSensorId(sensor) {
      return sensor?.pk ?? sensor?.sensor ?? null
    },
    // Silos com mais de 1 sensor renderizam apenas a leitura "virtual" (consolidada)
    // em workingSensors — aqui recuperamos os sensores físicos reais daquele silo
    // para exibir o card do Open Balena de cada um deles, não só da leitura virtual.
    balenaSensorsForSilo(sensor) {
      if (!sensor) return []
      if (sensor.silo == null) return [sensor]
      const siblings = this.sensorsFeedDataTarget.filter(
        (s) => s.silo === sensor.silo && s.sensor && !s.virtual
      )
      return siblings.length > 0 ? siblings : [sensor]
    },
    schedulePrioritySensorLoading() {
      if (this.priorityRenderTimeout) {
        clearTimeout(this.priorityRenderTimeout);
        this.priorityRenderTimeout = null;
      }

      const hasPriorityHint = this.selectedSiloFromRoute != null || this.selectedSensorFromRoute != null;

      // Sem hint de prioridade na rota → renderiza tudo normal.
      if (!hasPriorityHint) {
        this.shouldRenderRemainingSensors = true;
        return;
      }

      // Tem hint mas ainda não temos sensores carregados — esperamos o watcher
      // de `workingSensors` chamar de novo. Mantém o modo prioridade (não libera
      // os outros cards prematuramente).
      if (this.workingSensors.length === 0) {
        return;
      }

      // Tem hint, mas o silo solicitado não foi encontrado entre os sensores
      // disponíveis (ou só existe um silo) → libera todos.
      if (!this.selectedSensorForPriority || this.workingSensors.length === 1) {
        this.shouldRenderRemainingSensors = true;
        this.ensureScrollToSelectedSilo();
        return;
      }

      // Caso normal: renderiza só o card prioritário, libera os demais após
      // dar tempo dele disparar suas próprias requisições.
      this.shouldRenderRemainingSensors = false;
      this.priorityRenderTimeout = setTimeout(() => {
        this.shouldRenderRemainingSensors = true;
        this.ensureScrollToSelectedSilo();
      }, 1500);
    },
    ensureScrollToSelectedSilo() {
      // Re-try a few times to avoid race conditions with late card rendering.
      [0, 250, 700].forEach((delay) => {
        setTimeout(() => this.scrollToSelectedSilo(), delay)
      })
    },
    scrollToSelectedSilo() {
      if (!this.selectedSiloFromRoute && !this.selectedSensorFromRoute) return
      const tryScroll = (attempt = 0) => {
        this.$nextTick(() => {
          const bySensorSelector = this.selectedSensorFromRoute != null
            ? `[data-sensor="${this.selectedSensorFromRoute}"]`
            : null
          const bySiloSelector = this.selectedSiloFromRoute != null
            ? `[data-silo="${this.selectedSiloFromRoute}"]`
            : null
          const selectedSiloElement =
            (bySensorSelector ? this.$el?.querySelector(bySensorSelector) : null) ||
            (bySiloSelector ? this.$el?.querySelector(bySiloSelector) : null)

          if (selectedSiloElement) {
            selectedSiloElement.scrollIntoView({ behavior: "smooth", block: "start" })
            return
          }
          if (attempt < 6) {
            setTimeout(() => tryScroll(attempt + 1), 150)
          }
        })
      }
      tryScroll()
    },
    newRangeData() {
      // Evita disparos múltiplos enquanto já está atualizando.
      if (this.isReloading) return

      // Limpa timers anteriores caso o usuário tenha clicado de novo.
      if (this.reloadBannerTimeout) {
        clearTimeout(this.reloadBannerTimeout)
        this.reloadBannerTimeout = null
      }
      if (this.refreshAnimationTimeout) {
        clearTimeout(this.refreshAnimationTimeout)
        this.refreshAnimationTimeout = null
      }

      this.isReloading = true
      this.showRefreshAnimation = false

      // Toggle dispara os watchers nos componentes filhos (SmartFeedV3,
      // SmartFeedConsolidatedData, SmartfeedProfileHistory3) que vão fazer
      // o refetch dos seus próprios dados com as novas datas.
      this.seekNewRange = !this.seekNewRange

      // Após dar tempo dos filhos refazerem suas requisições principais,
      // escondemos o banner e disparamos a animação de atualização.
      this.reloadBannerTimeout = setTimeout(() => {
        this.isReloading = false
        this.showRefreshAnimation = true

        // Remove a classe da animação depois que ela terminou de rodar
        // (precisa ser removida senão não dispara de novo no próximo refresh).
        this.refreshAnimationTimeout = setTimeout(() => {
          this.showRefreshAnimation = false
        }, 700)
      }, 1500)
    },
    async fetchFoodByCompany () {
      try {
        if (!this.companyPk) return
        const cachedFoods = this.$store.getters.getFoodsByCompany(this.companyPk)
        if (Array.isArray(cachedFoods) && cachedFoods.length > 0) {
          this.$store.commit('setFoodByCompany', cachedFoods)
          return
        }
        const foods = await this.$store.dispatch('fetchFoodByCompany', this.companyPk)
        this.$store.commit('setFoodByCompany', foods)

      } catch (error) {
        console.log(error)
      }
    },
    openExportModal() {
      if (this.loadingExport) return;
      this.showExportModal = true;
    },
    closeExportModal() {
      this.showExportModal = false;
    },
    async onExportConfirm({ startDate, endDate, format: exportFormat }) {
      try {
        this.loadingExport = true;
        const realDataBySilo = await this.fetchProfilesForExport(startDate, endDate);
        if (!realDataBySilo || Object.keys(realDataBySilo).length === 0) {
          alert('Não há dados de perfis disponíveis para o período selecionado');
          return;
        }
        const locationInfo = {
          farmName: this.farm?.farm?.name || 'Granja',
          barnName: this.barn?.name || 'Galpão'
        };
        if (exportFormat === 'pdf') {
          await generateMultipleSilosPDF(realDataBySilo, locationInfo);
        } else {
          await generateMultipleSilosExcel(realDataBySilo, locationInfo);
        }
        this.closeExportModal();
      } catch (error) {
        console.error('Error al generar archivo:', error);
        alert(error.message || 'Erro ao gerar o arquivo');
      } finally {
        this.loadingExport = false;
      }
    },
    async fetchProfilesForExport(startDate, endDate) {
      const start = startDate || this.start_date;
      const end = endDate || this.end_date;
      const barn = this.barn ?? this.selectedFeed?.barn ?? this.listFeed?.find(f => f.barn?.pk)?.barn;
      const barnPk = barn?.pk ?? barn?.id;
      let companyPk = this.userLevel === 'pecsmart'
        ? (this.$store.getters.getTargetCompany?.pk ?? this.$store.getters.getTargetCompany?.id)
        : null;
      if (!companyPk && this.farm?.farm) {
        const farmCompany = this.farm.farm.company;
        companyPk = farmCompany?.pk ?? farmCompany?.id ?? farmCompany;
      }
      if (!companyPk) {
        companyPk = this.company?.pk ?? this.company?.id ?? this.company;
      }
      if (!companyPk) {
        const user = this.$store.getters.getCurrentUser;
        companyPk = user?.company?.pk ?? user?.company?.id ?? user?.company;
      }
      if (!barnPk || !companyPk) {
        throw new Error('Dados do galpão ou empresa não disponíveis');
      }
      const result = await this.$store.dispatch('fetchBarnProfilesHistory', {
        barn_pk: barnPk,
        start_date: start,
        end_date: end,
        company_pk: companyPk
      });
      const { profilesData, foods } = result;
      if (!profilesData || profilesData.length === 0 || profilesData.every(arr => !arr || arr.length === 0)) {
        return {};
      }
      const allProfiles = profilesData.flat().filter(Boolean);
      const profilesBySilo = {};
      allProfiles.forEach(profile => {
        const siloId = profile.silo;
        if (!profilesBySilo[siloId]) profilesBySilo[siloId] = [];
        profilesBySilo[siloId].push(profile);
      });
      const realDataBySilo = {};
      Object.entries(profilesBySilo).forEach(([siloId, profiles]) => {
        const sorted = profiles.sort((a, b) => new Date(a.datetime_fetched) - new Date(b.datetime_fetched));
        const filteredData = filterProfilesByMinLevelVariation(
          this.filterOriginalSiloData(sorted),
          getMinSiloLevelVariationPercent(this.companySettings)
        );
        const siloName = sorted[0]?.silo_name || `Silo ${siloId}`;
        realDataBySilo[siloName] = filteredData.map(row => ({
          ...row,
          food_name: this.getFoodNameFromFoods(row.food, foods),
          food_density: this.getFoodDensityFromFoods(row.food, foods)
        }));
      });
      return realDataBySilo;
    },
    getFoodNameFromFoods(foodId, foods) {
      if (!foods || !Array.isArray(foods)) return 'N/A';
      const food = foods.find(f => f.pk === foodId);
      return food ? food.name : 'N/A';
    },
    getFoodDensityFromFoods(foodId, foods) {
      if (!foods || !Array.isArray(foods)) return 'N/A';
      const food = foods.find(f => f.pk === foodId);
      return food && food.density != null ? food.density : 'N/A';
    },
    filterOriginalSiloData(data) {
      if (!data || !Array.isArray(data) || data.length === 0) {
        return [];
      }
      
      const stabilityFilterThreshold = 5;
      
      return data.reduce((acc, profile, idx, array) => {
        if (profile.level == null || profile.level <= 0) {
          return acc;
        }
        
        if (![array[idx - 1], array[idx - 5], array[idx - 9]].includes(undefined)) {
          const diffBehind = (i) => Math.abs(profile.level - array[idx - i].level);
          const isStable = [diffBehind(1), diffBehind(5), diffBehind(9)].every(diff => diff < parseInt(stabilityFilterThreshold));
          if (!isStable) {
            return acc;
          }
        }
        
        acc.push(profile);
        return acc;
      }, []);
    },
    getFoodNameById(foodId) {
      const food = this.allFoods.find(f => f.pk === foodId);
      return food ? food.name : 'N/A';
    },
    getFoodDensityById(foodId) {
      const food = this.allFoods.find(f => f.pk === foodId);
      return food ? food.density : 'N/A';
    },
  },
  computed: {
    allFoods() {
      return this.$store.getters?.getFoodByCompany ?? []
    },
    showConsolidatedChart(){
      return !this.companySettings?.settings?.client_frontend?.hide_feed_consume_chart ?? true
    },
    showProfileVisualization(){
      return this.companySettings?.settings?.client_frontend?.show_feed_profile_visualization ?? false
    },
    useLegacySiloProfileVisualization(){
      return this.companySettings?.settings?.client_frontend?.use_legacy_silo_profile_visualization ?? false
    },
    AIcombinedSensors(){
      return this.companySettings?.settings?.smartfeed_ai_combined_prediction ?? undefined
    },
    selectedSiloFromRoute() {
      const silo = this.$route?.query?.silo
      if (silo == null || silo === '') return null
      const parsed = Number(silo)
      return Number.isFinite(parsed) ? parsed : silo
    },
    selectedSensorFromRoute() {
      const sensor = this.$route?.query?.sensor
      if (sensor == null || sensor === '') return null
      const parsed = Number(sensor)
      return Number.isFinite(parsed) ? parsed : sensor
    },
    workingSensors() {
      if (this.sensorsFeedDataTarget.length > 0) {
        const siloGroups = {};
        this.sensorsFeedDataTarget.forEach(sensor => {
          if (!siloGroups[sensor.silo]) {
            siloGroups[sensor.silo] = [];
          }
          siloGroups[sensor.silo].push(sensor);
        });

       
        const filteredData = this.sensorsFeedDataTarget.filter(sensor => {
          const siloGroup = siloGroups[sensor.silo];
          if (siloGroup.length === 1) {
            return true; 
          }
          return sensor.virtual;
        });

        const validSensors = filteredData.filter(obj => obj.sensor)

        const sortedData = [...validSensors].sort((a, b) => {
          const nameA = a.silo_name || a.name || '';
          const nameB = b.silo_name || b.name || '';
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
        });
        
        return sortedData;
      }else {
        return []
      }
    },
    selectedSensorForPriority() {
      if (!this.selectedSiloFromRoute && !this.selectedSensorFromRoute) return null
      return this.workingSensors.find((sensor) => {
        const sameSensor = this.selectedSensorFromRoute != null && String(this.getSensorId(sensor)) === String(this.selectedSensorFromRoute)
        const sameSilo = this.selectedSiloFromRoute != null && String(sensor.silo) === String(this.selectedSiloFromRoute)
        return sameSensor || sameSilo
      }) || null
    },
    sensorsToRender() {
      if ((this.selectedSiloFromRoute == null && this.selectedSensorFromRoute == null) || this.shouldRenderRemainingSensors) {
        return this.workingSensors
      }
      return this.selectedSensorForPriority ? [this.selectedSensorForPriority] : this.workingSensors
    },
    barn() {
      return this.$store.getters.getBarnTarget
    },
    batch() {
      return this.$store.getters.getBatchTarget
    },
    listFeed() {
      return this.$store.getters.getSensorsListFeed
    },
    selectedFeed() {
      if (this.batch == null) {
        return this.listFeed.find(obj => obj.barn.pk == this.barn.pk)
      }
      return this.listFeed.find(obj => {
        if (obj.batch) {
          if (obj.barn.pk == this.barn.pk) {
            return obj
          }
        }
      })
    },
    sensorsFeedDataTarget() {
      return this.selectedFeed?.data ?? []
    },
    userLevel() {
      return this.$store.getters.getUserLevel
    },
    farm() {
      return this.$store.getters.getAllBarns.find((obj)=> {
        return obj.barn.find((barn)=>barn.pk == this.barn.pk)
      })
    },
    company() {
      return this.farm?.farm?.company
    },
    farmPk() {
      const farmTarget = this.$store.getters.getFarmTarget
      if (farmTarget != null) return farmTarget
      return this.farm?.farm?.pk ?? this.farm?.farm ?? null
    },
    companyPk() {
      return this.company?.pk ?? this.company?.id ?? this.company ?? null
    },
    companySettings(){
      return this.$store.getters.getCompanySettings 
    },
    useInformedWeight(){
      return this.companySettings?.settings?.client_frontend?.use_informed_max_silo_weight ?? false
    },
    sacaWeight() {
      return this.companySettings?.settings?.client_frontend?.weight_of_feed_bag ?? 60
    },
    startDate() {
      return this.getDefaultDateRange().start
    },
    endDate() {
      return this.getDefaultDateRange().end
    },
  },
  watch: {
    '$route.query.silo'() {
      this.schedulePrioritySensorLoading()
    },
    '$route.query.sensor'() {
      this.schedulePrioritySensorLoading()
    },
    workingSensors() {
      this.schedulePrioritySensorLoading()
    },
    shouldRenderRemainingSensors(value) {
      if (value) {
        this.ensureScrollToSelectedSilo()
      }
    }
  },
  async created(){
    if (sensorScreenBlocked(this.$store.getters.getCurrentUser, this.$store.getters.getCompanySettings)) {
      this.loading = false
      return
    }
    this.start_date = this.startDate
    this.end_date = this.endDate
    this.fetchFoodByCompany()
    if (this.userLevel == 'pecsmart') {
      this.loading = false
      this.start_date = this.startDate
      this.end_date = this.endDate
    }
    this.schedulePrioritySensorLoading()
    this.loading = false
  },
  beforeDestroy() {
    if (this.priorityRenderTimeout) {
      clearTimeout(this.priorityRenderTimeout)
      this.priorityRenderTimeout = null
    }
    if (this.reloadBannerTimeout) {
      clearTimeout(this.reloadBannerTimeout)
      this.reloadBannerTimeout = null
    }
    if (this.refreshAnimationTimeout) {
      clearTimeout(this.refreshAnimationTimeout)
      this.refreshAnimationTimeout = null
    }
  }
};
</script>

<style scoped>
.maxcontainer {
  width: 100%;
  max-width: 100%;
  padding: 0px;
  overflow-x: hidden;
  box-sizing: border-box;
}

.container-racao{
  min-width: 200px;
  display: flex;
  justify-content: center;
}

.container-racao button{
  min-width: 120px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s ease-out;
}

.container-racao-granja{
  display: flex;
  justify-content: end;
  margin-top: 48px;
}

.container-racao-granja button{
  min-width: 120px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s ease-out;
}

.smartfeed-data-container {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 20px;
  align-items: center;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
}

.smartfeed-card-wrapper {
  width: 100%;
  max-width: 760px;
  box-sizing: border-box;
}

.smartfeed-card-wrapper--legacy {
  max-width: 1000px;
}

.container-inputsDate{
  display: flex;
  margin:4px;
  margin-inline: 8px;
  align-items: center;
  justify-content: center;
}

.container-inputs-buton{
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-direction: row;
  margin-top: 48px;
}

.button-food{
  background-color: #1b4238;
  color: #fff;
  padding: 8px;
  padding-inline: 12px;
  border-radius: 8px;
  border: none;
}

.button-food:hover{
  background-color: #1e7e34;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
}

.container-dates{
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.container-buton{
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-date{
  margin-left: 12px; 
  max-width: 200px; 
  min-width:200px;
  padding-inline: 8px;
  padding-top: 4px;
  padding-bottom: 4px;
  border-radius: 8px ;
}

.buttomDate{
  margin:4px;
  padding: 8px;
  border-radius: 8px;
  background-color: #78b843 ;
  border: none;
  padding-left: 16px;
  padding-right: 16px;
  color: #fff;
  transition: background-color 0.2s ease-out;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
}

.buttomDate:hover{
  background-color: #1e7e34;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
}

/* Estilos para o botão de exportação */
.container-export {
  min-width: 120px;
  display: flex;
  justify-content: center;
}

.export-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  background-color: #1b4238;
  color: white;
  min-width: 120px;
  justify-content: center;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
}

.export-btn:hover:not(:disabled) {
  background-color: #1e7e34;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
}

.export-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.buttomDate:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background-color: #6e9f4f;
}

/* ---- Banner "Carregando novos dados..." ---- */
.reload-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 24px;
  margin: 16px auto 0;
  width: fit-content;
  max-width: 90%;
  background: linear-gradient(135deg, #1b4238 0%, #2f6b53 100%);
  color: #fff;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 6px 16px rgba(27, 66, 56, 0.25);
  letter-spacing: 0.2px;
}

.reload-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: reload-spinner-rotate 0.8s linear infinite;
}

@keyframes reload-spinner-rotate {
  to { transform: rotate(360deg); }
}

.reload-banner-enter-active,
.reload-banner-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.reload-banner-enter,
.reload-banner-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ---- Estado "atualizando" nos cards/gráfico ----
   Mantemos os dados visíveis com um leve esmaecer (sem blur) para que o
   usuário sempre tenha contexto na tela enquanto novos dados chegam. */
.is-refreshing {
  opacity: 0.85;
  pointer-events: none;
  transition: opacity 0.35s ease;
}

/* ---- Animação de "dados atualizados" ---- */
.just-refreshed {
  animation: data-refresh-pulse 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes data-refresh-pulse {
  0% {
    opacity: 0.85;
    transform: translateY(2px) scale(0.998);
  }
  60% {
    opacity: 1;
    transform: translateY(-1px) scale(1.001);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media screen and (max-width: 1090px) {
  .container-dates {
    flex-direction: column;
  }
}

@media screen and (max-width: 768px) {
  .container-dates {
    flex-wrap: wrap;
    gap: 12px;
    padding: 0 12px;
  }

  .container-inputsDate {
    width: 100%;
    max-width: 100%;
  }

  .input-date {
    min-width: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .smartfeed-data-container {
    padding: 12px;
    gap: 12px;
  }
}

@media screen and (max-width: 480px) {
  .container-dates {
    padding: 0 8px;
  }

  .container-buton,
  .container-racao,
  .container-export {
    width: 100%;
    min-width: 0;
  }

  .buttomDate,
  .button-food,
  .export-btn {
    width: 100%;
    min-width: 0;
  }
}

</style>
